import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Container, Row, Col, Card, Alert } from 'react-bootstrap'
import { formatCurrency, IsVehicleElectric } from '../../../helpers'
import ContactConsultant from 'components/contactConsultant'
import { AccordionCard } from '../../accordion'
import Tooltip from '../../tooltip'
import ContactUsDialog from '../../dialog/contactUsDialog'
import { PrimaryLink } from 'components/styledComponents/link'
import ProceedWithEstimateButton from '../../actionEvents/proceedWithEstimateButton'
import { applicationRoles } from '../../../constants/actionTypes'
import ThingsToknow from 'components/thingsToknow'
import { PrimaryButton } from '@mmsg/louie'
import { Modal } from 'react-bootstrap'
import { SummarySheet } from '../pdf/summarySheet'
import { Icon } from 'icons'
import RfbaSection from 'components/pages/proposal/rfbaSection'
import { useHistory } from 'react-router-dom'
import * as E from './endOfLeaseSectionElements'

const EndOfLeaseSection = ({
  data: {
    leaseResidualAmountIncGST,
    brand,
    userFirstName,
    consultantFirstName,
    year,
    make,
    family,
    vehicleId,
    vehicleDescription,
    payFrequency,
    residualPCT,
    opportunityId,
    retailPrice,
    priveawayPrice,
    isIndicativeProposal,
    fbtMethod,
    vehicleFBTValue,
  },
  novatedLeaseData,
  applicationRole,
  expandAccordion,
  pdfViewClick,
  isGov,
  isValid,
  theme,
}) => {
  let estimateID = useSelector(
    (state) => state.individualEstimate.selectedEstimateId
  )
  let hasSubmissionFailed = useSelector(
    (state) =>
      state.proceedEstimate && state.proceedEstimate.hasSubmissionFailed
  )
  let submissionErrorMsg = useSelector(
    (state) =>
      state.proceedEstimate && state.proceedEstimate.errorMsgOnProceedEstimate
  )
  
  let displayRetailPrice = (retailPrice ? retailPrice : 0) > priveawayPrice
  let showRFBA = useSelector((state) => IsVehicleElectric(state.individualEstimate.estimate, true)) 
    && fbtMethod && fbtMethod.toLowerCase() == 'exempt' 
    && vehicleFBTValue && vehicleFBTValue < 84916

  const [showSummarySheet, setShowSummarySheet] = useState(false)

  const toggleviewSummarySheet = () => {
    setShowSummarySheet(!showSummarySheet)
  }

  let history = useHistory()

  const GoBackToEstimatesPage = () => {
    if (applicationRole == applicationRoles.CONSULTANT) {
      history.push('/consultant/estimates')
    } else {
      history.push('/estimates')
    }
  }
  let buttonStyle = 'd-block mx-auto'
  if (!isValid) buttonStyle += ' readonly'
  return (
    <>
      <Modal show={showSummarySheet} onHide={toggleviewSummarySheet} size="lg">
        <Modal.Header
          closeButton
          style={{
            background: 'transparent',
            borderBottom: 'none',
            paddingBottom: 0,
          }}
        ></Modal.Header>
        <Modal.Body>
          <div>
            <SummarySheet novatedLeaseData={novatedLeaseData} exGst={true} />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'start' }}>
          <PrimaryButton extendInMob onClick={pdfViewClick}>
            View PDF
          </PrimaryButton>
          <PrimaryButton
            extendInMob
            invertColor
            onClick={toggleviewSummarySheet}
          >
            Close
          </PrimaryButton>
        </Modal.Footer>
      </Modal>

      <Container
        className="section mt-3 mb-5 print-page-break-before"
        id="endOfLeaseSection"
      >
        <h4 className="text-dark mb-4">
          And here’s what happens when your lease is up
        </h4>
        <Card className="mb-13rem card-shadow">
          <Card.Header className="border-bottom-0">
            <div className="text-small">
              Understanding your end of lease options
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="mb-24">
              <Col md={8} xs={12}>
                <div className="font-weight-bold">
                  <div className="d-flex mb-3">
                    <span className="mr-2">
                      How it works: Residual value
                      <Tooltip
                        header="Residual value"
                        text="The anticipated amount owing on the car at the end of the lease, as set and required by the ATO."
                      />{' '}
                    </span>
                  </div>
                </div>
                <Row className="mb-24">
                  <Col className="col--nobold font-color-indicative">
                    Thanks to your residual, novated leasing helps you keep
                    money in your pocket upfront. When your lease is up, you'll
                    owe the residual value. You can roll your residual into a
                    new lease, make it smaller by re-leasing the car, or pay it
                    out. We've calculated it for you upfront, just to make sure
                    there are no surprises down the road.
                  </Col>
                </Row>
              </Col>
              <Col md={4} xs={12} className="sub-section-residual-amount">
                <Card className="sub-section-focused">
                  <Card.Text>
                    <Row>
                      <Col className="col--nobold">
                        Total residual value after lease term (inc GST)
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-larger font-weight-bold">
                        {formatCurrency(leaseResidualAmountIncGST)}
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col className="col--nobold">
                        (Calculated at {residualPCT}%)
                      </Col>
                    </Row>
                  </Card.Text>
                </Card>
              </Col>
            </Row>
            <Row className="mb-24">
              <Col>
                <div className="font-weight-bold mb-3">
                  What are your options?
                </div>
                <div className="col--nobold font-color-indicative">
                  It’s always nice to get a feel for your options from the get
                  go. But don’t worry, we’ll be in touch well before your lease
                  ends to help you figure out which direction you’d like to take
                  next.
                </div>
              </Col>
            </Row>
            <Row className="text-center sub-section-lease-options">
              <Col sm>
                <Icon iconType="Upgrade" size={3} className="mb-2" />
                <div className="font-weight-bold sub-section-lease-options__heading">
                  Upgrade
                </div>
                <div className="text-small mb-24 col--nobold">
                  Relive that new car feeling and cash in on the convenience of
                  upgrading with {brand}.
                </div>
              </Col>
              <Col sm>
              <Icon iconType='ReLease' size={3}  className="mb-2"/>
                <div className="font-weight-bold sub-section-lease-options__heading">
                  Re-lease
                </div>
                <div className="text-small mb-24">
                  Keep enjoying the car (and estimated tax savings) by extending
                  your {brand} lease.
                </div>
              </Col>
              <Col sm>
                <Icon iconType='PayItOut' size={3} className="mb-2"/>
                <div className="font-weight-bold sub-section-lease-options__heading">
                  Pay it out
                </div>
                <div className="text-small mb-24">
                  Whether you sell the car or buy it, you'll need to pay out
                  your residual.
                </div>
              </Col>
            </Row>

            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-3"
              title="Can someone help me sell my other car if I upgrade?"
            >
              <div>
                You bet. If you want to relive that new car feeling once your
                lease is up, we'll make upgrading easy. Our team will help you
                trade in the car on your current lease, and get you inside a new
                one ASAP.
              </div>
              <div>
                Or, if you decide to pay out the car, we can help you sell it to
                cover part (if not all) of the residual. This is an optional
                service provided by Oly. You are not required to use this
                service to help sell your car if you upgrade.
              </div>
            </AccordionCard>
            <AccordionCard
              expandAccordion={expandAccordion}
              title="Do I have to pay the residual?"
            >
              <div>
                Yep, the residual is an ATO-legislated amount calculated as a
                percentage of the car's original value. We calculate it for you
                upfront so you know what you’re up for. Be sure to chat with a
                Oly consultant near the end of your lease about your options
                for paying out the amount.
              </div>
            </AccordionCard>
          </Card.Body>
        </Card>
          {showRFBA && (
          <RfbaSection
            pdf={false}
            title="Electric Vehicle FBT Exemption - What you need to know"
          />
        )}
        <Card body className="card-shadow">
          <div className="font-weight-bold text-brand mb-2">
            Congratulations {userFirstName}! You’re ready to drive your salary
            further with Oly
          </div>
          <div className="font-color-indicative">
            Like what you see? You can now lock in that
            {vehicleId && (
              <>
                {' '}
                {year} {make} {family}{' '}
              </>
            )}
            {!vehicleId && <> {vehicleDescription} </>}
            and proceed with this estimate. If you want to make changes, simply
            contact your consultant.
          </div>
          <div className="print hide-to-print">
            {applicationRole &&
              applicationRole !== applicationRoles.CONSULTANT && (
                <>
                  <Row>
                    <Col className="mt-3 mb-3 mx-auto" sm={12} lg={6}>
                      {!isIndicativeProposal && isValid ? (
                        <ProceedWithEstimateButton
                          opportunityId={opportunityId}
                          estimateId={estimateID}
                          className={buttonStyle}
                          size="sm"
                          variant="primary"
                          launchText="Proceed with this estimate"
                        />
                      ) : (
                        <PrimaryButton disabled>
                          Proceed with this estimate
                        </PrimaryButton>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      className="mx-auto mt-3 mb-3 sm-justify-center contact-consultant"
                      sm={12}
                      lg={6}
                    >
                      <ContactUsDialog
                        className="link"
                        theme="link"
                        launchText="Contact my consultant"
                        inlineText
                      />
                    </Col>
                  </Row>
                  {hasSubmissionFailed &&
                    submissionErrorMsg !== '' &&
                    submissionErrorMsg !=
                      'Sparta vehicle no longer available.' && (
                      <Row>
                        <Col className="col--nobold errorMsg show mb-2 text-center">
                          {submissionErrorMsg.replace(
                            /(500)|(405)|(401)|(400)/g,
                            ''
                          )}{' '}
                          <ContactConsultant
                            textPrefix="Please"
                            textUnderline="contact your consultant"
                            textPostfix="to discuss."
                          />
                        </Col>
                      </Row>
                    )}
                </>
              )}
            {applicationRole &&
              applicationRole === applicationRoles.CONSULTANT && (
                <>
                  {' '}
                  <Row>
                    <div className="mt-3 mb-3 mx-auto">
                      <PrimaryButton disabled>
                        Proceed with this estimate
                      </PrimaryButton>
                    </div>
                  </Row>
                  <Row>
                    <div className="mx-auto">
                      <PrimaryLink disabled>Contact my consultant</PrimaryLink>
                    </div>
                  </Row>
                </>
              )}
          </div>
        </Card>
        {hasSubmissionFailed &&
          submissionErrorMsg == 'Sparta vehicle no longer available.' && (
            <Alert variant="danger" className="text-left mt-3 w-100">
              <E.styledAlertIcon />
              <div className="text-small ml-3 mb-1">
                <b>
                  Unfortunately this car is no longer available, as it was part
                  of a limited stock order.
                </b>
                <p>
                  You can view your other{' '}
                  <E.styledLink onClick={GoBackToEstimatesPage}>
                    estimates
                  </E.styledLink>{' '}
                  or{' '}
                  {applicationRole &&
                    applicationRole !== applicationRoles.CONSULTANT && (
                      <>
                        <ContactUsDialog
                          className="link"
                          theme="link"
                          launchText="chat to your consultant"
                        />
                      </>
                    )}{' '}
                  about other cars.
                </p>
              </div>
            </Alert>
          )}
      </Container>
      <ThingsToknow
        fbtMethod={fbtMethod} displayRetailPrice={displayRetailPrice} financierEstablishmentFee={novatedLeaseData.proposalEstimate.financierEstablishmentFee}
      />
    </>
  )
}

export default EndOfLeaseSection
